import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { LanguageMenu } from "./LanguageMenu";
import { useLanguage } from "@/i18n/LanguageContext";
import { scrollToId } from "@/lib/scroll";

const NAV_LINKS = [
  { id: "about", key: "about", testid: "nav-link-about" },
  { id: "download", key: "download", testid: "nav-link-download" },
  { id: "support", key: "support", testid: "nav-link-support" },
];

export const Header = () => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 250 : 0);
  };

  return (
    <>
      <header
        data-testid="teamup-header-nav"
        className="fixed inset-x-0 top-0 z-50 border-b border-brand/10 bg-pitch/80 backdrop-blur-xl"
      >
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
          <button type="button" data-testid="teamup-logo-home" onClick={() => window.__lenis?.scrollTo(0, { duration: 1.2 })} aria-label="TeamUp home">
            <Logo />
          </button>

          <nav className="hidden items-center gap-9 md:flex">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                type="button"
                data-testid={l.testid}
                onClick={() => go(l.id)}
                className="group relative font-mono text-xs uppercase tracking-[0.2em] text-white/60 transition-colors duration-300 hover:text-white"
              >
                {t.nav[l.key]}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-brand transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <LanguageMenu />
            <button
              type="button"
              data-testid="teamup-header-download-cta"
              onClick={() => go("download")}
              className="group hidden items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white transition-transform duration-300 hover:scale-[1.04] active:scale-95 sm:flex"
            >
              {t.nav.cta}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              type="button"
              data-testid="mobile-menu-button"
              onClick={() => setOpen((v) => !v)}
              className="grid h-10 w-10 place-items-center rounded-full border border-white/10 text-white md:hidden"
              aria-label="Menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-pitch/95 px-8 backdrop-blur-2xl md:hidden"
          >
            <nav className="flex flex-col gap-2">
              {NAV_LINKS.map((l, i) => (
                <div key={l.id} className="overflow-hidden">
                  <motion.button
                    type="button"
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    exit={{ y: "110%" }}
                    transition={{ delay: 0.08 + i * 0.07, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    onClick={() => go(l.id)}
                    className="font-display text-4xl font-bold uppercase tracking-tight text-white transition-colors duration-300 hover:text-brand"
                  >
                    {t.nav[l.key]}
                  </motion.button>
                </div>
              ))}
            </nav>
            <motion.button
              type="button"
              data-testid="mobile-menu-download-cta"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.4 }}
              onClick={() => go("download")}
              className="mt-10 flex w-fit items-center gap-2 rounded-full bg-brand px-7 py-3.5 font-semibold text-white"
            >
              {t.nav.cta}
              <ArrowUpRight className="h-4 w-4" />
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
